import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Chip,
    Avatar,
} from "@mui/material";
import {
    ArrowBackIos as BackIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
    LocationOn as LocationIcon,
    Group as ContactsIcon,
    FolderSpecial as ProjectsIcon,
    Description as QuotesIcon,
    Phone as PhoneIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import CompanyService from "../services/Company.service";
import ContactService from "../services/Contact.service";
import ProjectService from "../services/Project.service";
import QuoteService from "../services/Quote.service";

function LabelValue({ label, value }) {
    if (!value && value !== 0) return null;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
            >
                {label}
            </Typography>
            <Typography variant="body2">{value}</Typography>
        </Box>
    );
}

function initialsOf(name) {
    return name
        ? name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
        : "?";
}

function CompanyDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [company, setCompany] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [projects, setProjects] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState("info");

    useEffect(() => {
        setLoading(true);
        setError("");
        Promise.all([
            CompanyService.get(id),
            ContactService.getByCompany(id),
            ProjectService.list({ company: id }),
            QuoteService.list({ company: id }),
        ])
            .then(([companyRes, contactRes, projectRes, quoteRes]) => {
                setCompany(companyRes.data);
                setContacts(contactRes.data?.results ?? contactRes.data ?? []);
                setProjects(projectRes.data?.results ?? projectRes.data ?? []);
                setQuotes(quoteRes.data?.results ?? quoteRes.data ?? []);
            })
            .catch(() => setError("Failed to load company details."))
            .finally(() => setLoading(false));
    }, [id]);

    const toggle = (panel) => (_, isOpen) => setExpanded(isOpen ? panel : false);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !company) {
        return (
            <Box>
                <IconButton size="small" onClick={() => navigate(-1)} sx={{ mb: 1 }}>
                    <BackIcon fontSize="small" />
                </IconButton>
                <Alert severity="error">{error || "Company not found."}</Alert>
            </Box>
        );
    }

    const phones = Array.isArray(company.phone) ? company.phone : [];
    const addresses = company.addresses || [];

    return (
        <Box>
            {/* ── Header ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <IconButton size="small" onClick={() => navigate(-1)}>
                    <BackIcon fontSize="small" />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ fontFamily: "Montserrat, sans-serif" }}
                        noWrap
                    >
                        {company.name}
                    </Typography>
                    {company.website && (
                        <Typography
                            component="a"
                            href={company.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            color="primary"
                            sx={{ textDecoration: "none" }}
                        >
                            {company.website}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* ── Company Info ── */}
            <Accordion
                expanded={expanded === "info"}
                onChange={toggle("info")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InfoIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Company Information
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {phones.length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                                sx={{ textTransform: "uppercase", letterSpacing: 0.4, display: "block", mb: 0.5 }}
                            >
                                Phone
                            </Typography>
                            {phones.map((ph) => (
                                <Box key={ph.id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <PhoneIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                                    <Typography
                                        component="a"
                                        href={`tel:${ph.phone_number}`}
                                        variant="body2"
                                        color="primary"
                                        sx={{ textDecoration: "none" }}
                                    >
                                        {ph.phone_number}
                                    </Typography>
                                    {ph.phone_type && (
                                        <Typography variant="caption" color="text.secondary">
                                            · {ph.phone_type}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                    {company.fax && (
                        <LabelValue label="Fax" value={company.fax.phone_number} />
                    )}
                    {!company.website && !phones.length && !company.fax && (
                        <Typography variant="body2" color="text.secondary">
                            No contact details on file.
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Addresses ── */}
            {addresses.length > 0 && (
                <Accordion
                    expanded={expanded === "addresses"}
                    onChange={toggle("addresses")}
                    disableGutters
                    elevation={0}
                    sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <LocationIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle2" fontWeight={600}>
                                Addresses
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                        <List disablePadding>
                            {addresses.map((addr, idx) => (
                                <React.Fragment key={addr.id ?? idx}>
                                    {idx > 0 && <Divider component="li" />}
                                    <ListItem sx={{ py: 1, px: 2 }}>
                                        <Box>
                                            <Typography variant="body2">{addr.address}</Typography>
                                            {addr.address_line2 && (
                                                <Typography variant="body2">{addr.address_line2}</Typography>
                                            )}
                                            <Typography variant="body2" color="text.secondary">
                                                {[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}
                                            </Typography>
                                        </Box>
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* ── Contacts ── */}
            <Accordion
                expanded={expanded === "contacts"}
                onChange={toggle("contacts")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ContactsIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Contacts
                        </Typography>
                        {contacts.length > 0 && (
                            <Chip label={contacts.length} size="small" sx={{ height: 18, fontSize: 11 }} />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {contacts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No contacts linked to this company.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {contacts.map((c, idx) => (
                                <React.Fragment key={c.id}>
                                    {idx > 0 && <Divider component="li" />}
                                    <ListItemButton
                                        onClick={() => navigate(`/crm/contacts/${c.id}`)}
                                        sx={{ py: 1, px: 2 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 44 }}>
                                            <Avatar sx={{ width: 32, height: 32, fontSize: 12, bgcolor: "primary.main" }}>
                                                {initialsOf(c.name)}
                                            </Avatar>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={c.name}
                                            secondary={c.job_title || c.email}
                                            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                                            secondaryTypographyProps={{ variant: "caption" }}
                                        />
                                        {c.is_archived && (
                                            <Chip label="Archived" size="small" sx={{ mr: 1 }} />
                                        )}
                                        <ChevronRightIcon fontSize="small" color="action" />
                                    </ListItemButton>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Projects ── */}
            <Accordion
                expanded={expanded === "projects"}
                onChange={toggle("projects")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ProjectsIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Projects
                        </Typography>
                        {projects.length > 0 && (
                            <Chip label={projects.length} size="small" sx={{ height: 18, fontSize: 11 }} />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {projects.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No projects linked to this company.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {projects.map((p, idx) => (
                                <React.Fragment key={p.id}>
                                    {idx > 0 && <Divider component="li" />}
                                    <ListItemButton
                                        onClick={() => navigate(`/projects/${p.id}`)}
                                        sx={{ py: 1, px: 2 }}
                                    >
                                        <ListItemText
                                            primary={`${p.number} · ${p.name}`}
                                            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                                        />
                                        {p.is_complete && (
                                            <Chip label="Complete" size="small" color="success" sx={{ mr: 1 }} />
                                        )}
                                        <ChevronRightIcon fontSize="small" color="action" />
                                    </ListItemButton>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Quotes ── */}
            <Accordion
                expanded={expanded === "quotes"}
                onChange={toggle("quotes")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <QuotesIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Quotes
                        </Typography>
                        {quotes.length > 0 && (
                            <Chip label={quotes.length} size="small" sx={{ height: 18, fontSize: 11 }} />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {quotes.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No quotes linked to this company.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {quotes.map((q, idx) => (
                                <React.Fragment key={q.id}>
                                    {idx > 0 && <Divider component="li" />}
                                    <ListItemButton
                                        onClick={() => navigate(`/crm/quotes/${q.id}`)}
                                        sx={{ py: 1, px: 2 }}
                                    >
                                        <ListItemText
                                            primary={`${q.number} · ${q.name}`}
                                            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                                        />
                                        {q.is_complete && (
                                            <Chip label="Won" size="small" color="success" sx={{ mr: 1 }} />
                                        )}
                                        <ChevronRightIcon fontSize="small" color="action" />
                                    </ListItemButton>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}

export default CompanyDetail;
